import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Icon from './Icon';
import { useTransactions } from '../contexts/TransactionContext';
import useInsightsData from '../hooks/useInsightsData';
import BalanceCard from './analytics/BalanceCard';
import SummaryCard from './analytics/SummaryCard';
import PatternCard from './analytics/PatternCard';
import AnomalyDetectionCard from './analytics/AnomalyDetectionCard';
import BudgetManagementCard from './analytics/BudgetManagementCard';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

/* 차트 색상 — 디자인 토큰 미러링 */
const CHART_COLORS = {
  income: '#548164',
  expense: '#C4554D',
  incomeBg: 'rgba(84, 129, 100, 0.1)',
  expenseBg: 'rgba(196, 85, 77, 0.1)',
};

const Analytics = () => {
  const { transactions } = useTransactions();
  const [timeRange, setTimeRange] = useState('6months');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  const customDateRange = customStartDate && customEndDate
    ? { start: customStartDate, end: customEndDate }
    : null;

  const { balanceMetrics, summaryData, patternData, hasEnoughData } = useInsightsData(
    transactions,
    timeRange,
    customDateRange
  );

  if (!transactions || transactions.length === 0) {
    return (
      <div className="card">
        <h2><Icon name="bar_chart" /> 상세 분석</h2>
        <div className="empty-state">
          <p className="empty-state-title">분석할 거래 데이터가 없습니다.</p>
          <p className="empty-state-description">거래를 추가한 후 다시 확인해주세요.</p>
        </div>
      </div>
    );
  }

  // 시간 범위별 데이터 생성
  const getTimeRangeData = () => {
    const now = new Date();
    let periods = [];
    let labels = [];

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 31) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const dateStr = d.toISOString().slice(0, 10);
          periods.push(dateStr);
          labels.push(d.getDate().toString());
        }
      } else {
        for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= end; d.setMonth(d.getMonth() + 1)) {
          const monthStr = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
          periods.push(monthStr);
          labels.push(d.toLocaleDateString('ko-KR', { month: 'short', year: '2-digit' }));
        }
      }
    } else if (timeRange === '7days') {
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().slice(0, 10);
        periods.push(dateStr);
        labels.push(date.toLocaleDateString('ko-KR', { weekday: 'short' }));
      }
    } else if (timeRange === '30days') {
      for (let i = 29; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().slice(0, 10);
        periods.push(dateStr);
        labels.push(date.getDate().toString());
      }
    } else if (timeRange === '3months') {
      for (let i = 2; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        periods.push(monthStr);
        labels.push(date.toLocaleDateString('ko-KR', { month: 'short', year: '2-digit' }));
      }
    } else if (timeRange === '6months') {
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        periods.push(monthStr);
        labels.push(date.toLocaleDateString('ko-KR', { month: 'short', year: '2-digit' }));
      }
    } else if (timeRange === '12months') {
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        periods.push(monthStr);
        labels.push(date.toLocaleDateString('ko-KR', { month: 'short', year: '2-digit' }));
      }
    }
    
    const incomeData = [];
    const expenseData = [];

    periods.forEach(period => {
      const periodTransactions = transactions.filter(t => {
        if (timeRange === '7days' || timeRange === '30days' || (timeRange === 'custom' && period.includes('-'))) {
          return t.date.slice(0, 10) === period;
        } else {
          return t.date.slice(0, 7) === period;
        }
      });
      
      const income = periodTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      const expense = periodTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + Number(t.amount), 0);
      
      incomeData.push(income);
      expenseData.push(expense);
    });

    return { labels, incomeData, expenseData };
  };

  const { labels, incomeData, expenseData } = getTimeRangeData();

  // 차트 데이터 설정
  const trendChartData = {
    labels,
    datasets: [
      {
        label: '수입',
        data: incomeData,
        borderColor: CHART_COLORS.income,
        backgroundColor: CHART_COLORS.incomeBg,
        tension: 0.4,
      },
      {
        label: '지출',
        data: expenseData,
        borderColor: CHART_COLORS.expense,
        backgroundColor: CHART_COLORS.expenseBg,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      datalabels: {
        display: (context) => {
          return context.dataset.data[context.dataIndex] > 0;
        },
        color: (context) => context.datasetIndex === 0 ? '#548164' : '#C4554D',
        font: { size: 10, weight: '500' },
        anchor: 'end',
        align: 'top',
        formatter: (value) => value.toLocaleString('ko-KR'),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toLocaleString('ko-KR') + '원';
          }
        }
      }
    },
  };

  // 통계 계산
  const totalIncome = incomeData.reduce((sum, val) => sum + val, 0);
  const totalExpense = expenseData.reduce((sum, val) => sum + val, 0);
  const avgIncome = totalIncome / incomeData.length;
  const avgExpense = totalExpense / expenseData.length;
  const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpense) / totalIncome * 100).toFixed(1) : 0;

  // 저축률 차트 데이터 (기간별 월 단위, -100~100% 클램핑)
  const savingsChartData = {
    labels,
    datasets: [
      {
        label: '저축률 (%)',
        data: incomeData.map((income, i) => {
          if (income === 0) return 0;
          const rate = ((income - expenseData[i]) / income) * 100;
          return Math.max(-100, Math.min(100, Math.round(rate * 10) / 10));
        }),
        borderColor: CHART_COLORS.income,
        backgroundColor: 'rgba(84, 129, 100, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: CHART_COLORS.income,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 5,
      },
    ],
  };

  const savingsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      datalabels: {
        display: true,
        color: '#548164',
        font: { size: 11, weight: '600' },
        anchor: 'end',
        align: 'top',
        formatter: (value) => value + '%',
      },
    },
    scales: {
      y: {
        min: -100,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
  };

  // 현재 월 계산
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // 기간 필터링된 거래 계산
  const filteredTransactions = (() => {
    const today = new Date();
    let startDate;

    if (timeRange === 'custom' && customStartDate && customEndDate) {
      startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      return transactions.filter((t) => {
        const d = new Date(t.date);
        return d >= startDate && d <= endDate;
      });
    }

    switch (timeRange) {
      case '7days':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        break;
      case '3months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        break;
      case '6months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
        break;
      case '12months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 12, today.getDate());
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    }

    return transactions.filter((t) => new Date(t.date) >= startDate);
  })();

  return (
    <div>
      {/* 컨트롤 패널 */}
      <div className="card">
        <h2 className="analytics-page-title"><Icon name="bar_chart" /> 상세 분석</h2>
        
        {/* 기간 선택 */}
        <div className="analytics-period-section">
          <div className="analytics-tab-group">
            {[
              { value: '7days', label: '7일' },
              { value: '30days', label: '30일' },
              { value: '3months', label: '3개월' },
              { value: '6months', label: '6개월' },
              { value: '12months', label: '12개월' },
              { value: 'custom', label: '직접 설정' }
            ].map(period => (
              <button
                key={period.value}
                onClick={() => {
                  setTimeRange(period.value);
                  setShowCustomRange(period.value === 'custom');
                }}
                className={`analytics-tab${timeRange === period.value ? ' analytics-tab--active' : ''}`}
              >
                {period.label}
              </button>
            ))}
          </div>
          
          {/* 사용자 정의 기간 선택 */}
          {showCustomRange && (
            <div className="analytics-custom-range">
              <div className="analytics-custom-range__row">
                <div>
                  <label>시작일</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label>종료일</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => {
                    if (customStartDate && customEndDate) {
                      // 사용자 정의 기간 적용 로직은 기존 getTimeRangeData 함수에서 처리
                    }
                  }}
                  className="btn btn-primary"
                >
                  적용
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 주요 지표 */}
      <div className="card">
        <h3><Icon name="trending_up" /> 주요 지표</h3>
        <div className="analytics-metrics-grid">
          <div className="analytics-metric-card">
            <h4 className="analytics-metric-card h4--income">평균 수입</h4>
            <p>{avgIncome.toLocaleString('ko-KR')}원</p>
          </div>
          <div className="analytics-metric-card">
            <h4 className="analytics-metric-card h4--expense">평균 지출</h4>
            <p>{avgExpense.toLocaleString('ko-KR')}원</p>
          </div>
          <div className="analytics-metric-card">
            <h4 className="analytics-metric-card h4--primary">저축률</h4>
            <p>{savingsRate}%</p>
          </div>
        </div>
      </div>

      {/* 수입/지출 추이 차트 */}
      <div className="card">
        <h3>수입/지출 추이</h3>
        <div style={{ height: '400px' }}>
          <Line data={trendChartData} options={chartOptions} />
        </div>
      </div>

      {/* 저축률 추이 차트 */}
      <div className="card">
        <h3>저축률 추이</h3>
        <div style={{ height: '300px' }}>
          <Line data={savingsChartData} options={savingsChartOptions} />
        </div>
      </div>

      {/* 이상 지출 + 예산 관리 (2-column grid) */}
      <div className="analytics-grid">
        <AnomalyDetectionCard
          transactions={transactions}
          filteredTransactions={filteredTransactions}
        />
        <BudgetManagementCard currentMonth={currentMonth} />
      </div>

      {/* InsightsCards */}
      {hasEnoughData && (
        <div className="insights-cards-section">
          <BalanceCard balanceMetrics={balanceMetrics} />
          <SummaryCard summaryData={summaryData} />
          <PatternCard patternData={patternData} />
        </div>
      )}
    </div>
  );
};

export default Analytics;
